import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';

/* --------------------------------------------------------------------------
   TikTok photo slideshow editor.

   Every slide is drawn by drawSlide(), both for the on-screen editor and for
   export, so what the user sees is exactly what gets burned into the
   1080×1920 JPEG that is uploaded. TikTok receives finished images; the text
   is part of the picture.
   -------------------------------------------------------------------------- */

export const SLIDE_W = 1080;
export const SLIDE_H = 1920;
export const MAX_SLIDES = 35;   // TikTok's limit for photo posts

const FONT_FAMILY = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const MAX_TEXT_WIDTH = SLIDE_W * 0.84;

let nextKey = 0;

export function newSlide(file, img) {
  return {
    key: ++nextKey,
    name: file.name,
    src: URL.createObjectURL(file),
    img,
    fit: 'fill',          // 'fill' crops to fill the screen; 'fit' keeps the whole image
    text: '',
    x: 0.5,               // text centre, as a fraction of the slide
    y: 0.72,
    size: 64,             // font size in px on the 1080-wide slide
    style: 'box',         // 'box': white background behind each line; 'outline': white edge
    opacity: 1,           // opacity of the white part
  };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/* Break text into lines that fit, respecting the user's own line breaks. */
function wrapLines(ctx, text) {
  const out = [];
  for (const para of String(text).split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(''); continue; }
    let line = words[0];
    for (const w of words.slice(1)) {
      if (ctx.measureText(line + ' ' + w).width <= MAX_TEXT_WIDTH) line += ' ' + w;
      else { out.push(line); line = w; }
    }
    out.push(line);
  }
  // Drop blank lines at the edges, keep blank lines between paragraphs.
  while (out.length && !out[0].trim()) out.shift();
  while (out.length && !out[out.length - 1].trim()) out.pop();
  return out;
}

/* Where the text sits and how big its box is, in slide pixels. */
export function layoutText(ctx, slide) {
  if (!slide.text || !slide.text.trim()) return null;
  ctx.font = `700 ${slide.size}px ${FONT_FAMILY}`;
  const lines = wrapLines(ctx, slide.text);
  if (!lines.length) return null;

  const lineH = slide.size * 1.28;
  const padX = slide.size * 0.38;
  const widths = lines.map((l) => ctx.measureText(l).width);
  const boxW = Math.max(...widths) + padX * 2;
  const boxH = lineH * lines.length + slide.size * 0.2;
  return { lines, widths, lineH, padX, boxW, boxH, cx: slide.x * SLIDE_W, cy: slide.y * SLIDE_H };
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCover(ctx, img) {
  const s = Math.max(SLIDE_W / img.naturalWidth, SLIDE_H / img.naturalHeight);
  const w = img.naturalWidth * s, h = img.naturalHeight * s;
  ctx.drawImage(img, (SLIDE_W - w) / 2, (SLIDE_H - h) / 2, w, h);
}

function drawContain(ctx, img) {
  const s = Math.min(SLIDE_W / img.naturalWidth, SLIDE_H / img.naturalHeight);
  const w = img.naturalWidth * s, h = img.naturalHeight * s;
  ctx.drawImage(img, (SLIDE_W - w) / 2, (SLIDE_H - h) / 2, w, h);
}

/** Draw one slide onto a 1080×1920 context. */
export function drawSlide(ctx, slide) {
  ctx.save();
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);

  if (slide.img) {
    if (slide.fit === 'fill') {
      drawCover(ctx, slide.img);
    } else {
      // The whole image over a blurred, darkened copy of itself. Browsers
      // without canvas filters get a plain darkened copy instead.
      if ('filter' in ctx) ctx.filter = 'blur(40px) brightness(0.6)';
      drawCover(ctx, slide.img);
      ctx.filter = 'none';
      if (!('filter' in ctx)) { ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.fillRect(0, 0, SLIDE_W, SLIDE_H); }
      drawContain(ctx, slide.img);
    }
  }

  const t = layoutText(ctx, slide);
  if (t) {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const top = t.cy - t.boxH / 2 + slide.size * 0.1;

    t.lines.forEach((line, i) => {
      const lineCy = top + t.lineH * (i + 0.5);
      if (!line.trim()) return;

      if (slide.style === 'box' && slide.opacity > 0) {
        const w = t.widths[i] + t.padX * 2;
        ctx.fillStyle = `rgba(255,255,255,${slide.opacity})`;
        roundRect(ctx, t.cx - w / 2, lineCy - t.lineH / 2, w, t.lineH, slide.size * 0.28);
        ctx.fill();
      }
      if (slide.style === 'outline' && slide.opacity > 0) {
        ctx.lineJoin = 'round';
        ctx.lineWidth = slide.size * 0.2;
        ctx.strokeStyle = `rgba(255,255,255,${slide.opacity})`;
        ctx.strokeText(line, t.cx, lineCy);
      }
      ctx.fillStyle = '#000';
      ctx.fillText(line, t.cx, lineCy);
    });
  }
  ctx.restore();
}

/** Render a slide to the JPEG that gets uploaded. */
export async function renderSlideToFile(slide, index) {
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const canvas = document.createElement('canvas');
  canvas.width = SLIDE_W;
  canvas.height = SLIDE_H;
  drawSlide(canvas.getContext('2d'), slide);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) throw new Error(`Slide ${index + 1} could not be rendered.`);
  return new File([blob], `slide-${index + 1}.jpg`, { type: 'image/jpeg' });
}

/* A slide drawn on a canvas, sized by CSS. */
function SlideCanvas({ slide, style, canvasRef, ...rest }) {
  const localRef = useRef(null);
  const ref = canvasRef || localRef;
  useEffect(() => {
    const c = ref.current;
    if (c) drawSlide(c.getContext('2d'), slide);
  }, [slide, ref]);
  return <canvas ref={ref} width={SLIDE_W} height={SLIDE_H} style={{ display: 'block', width: '100%', height: 'auto', ...style }} {...rest} />;
}

const btn = (active) => ({
  flex: 1, padding: '6px 8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  border: '1px solid ' + (active ? '#0f172a' : '#cbd5e1'), borderRadius: 8,
  background: active ? '#0f172a' : '#fff', color: active ? '#fff' : '#334155',
});

function SlideshowEditor({ slides, setSlides }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const selected = slides.find((s) => s.key === selectedKey) || slides[0] || null;
  const index = selected ? slides.indexOf(selected) : -1;

  // Release image memory when the editor goes away.
  useEffect(() => () => slidesRef.current.forEach((s) => URL.revokeObjectURL(s.src)), []);

  const update = (key, patch) => setSlides((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const onDrop = useCallback(async (accepted, rejected) => {
    if (rejected.length) toast.error('Use JPG, PNG or WebP images.');
    const room = MAX_SLIDES - slidesRef.current.length;
    if (room <= 0) return toast.error(`TikTok allows up to ${MAX_SLIDES} photos.`);
    if (accepted.length > room) toast.warn(`Only the first ${room} image${room === 1 ? ' was' : 's were'} added — TikTok allows ${MAX_SLIDES}.`);

    const added = [];
    for (const file of accepted.slice(0, room)) {     // in the order chosen
      const s = newSlide(file, null);
      try {
        s.img = await loadImage(s.src);
        added.push(s);
      } catch (e) {
        URL.revokeObjectURL(s.src);
        toast.error(`${file.name} could not be opened.`);
      }
    }
    if (added.length) {
      setSlides((prev) => [...prev, ...added]);
      setSelectedKey((k) => k || added[0].key);
    }
  }, [setSlides]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: true,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
    disabled: slides.length >= MAX_SLIDES,
  });

  const remove = (key) => setSlides((prev) => {
    const gone = prev.find((s) => s.key === key);
    if (gone) URL.revokeObjectURL(gone.src);
    const next = prev.filter((s) => s.key !== key);
    if (key === selectedKey) setSelectedKey(next[0]?.key ?? null);
    return next;
  });

  const move = (i, delta) => setSlides((prev) => {
    const to = i + delta;
    if (to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[to]] = [next[to], next[i]];
    return next;
  });

  // ── Dragging the text on the stage ────────────────────────────────────────
  const toSlideCoords = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * SLIDE_W, y: ((e.clientY - r.top) / r.height) * SLIDE_H };
  };

  const clampCentre = (slide, cx, cy) => {
    const t = layoutText(stageRef.current.getContext('2d'), { ...slide, x: 0.5, y: 0.5 });
    const hw = t ? Math.min(t.boxW / 2, SLIDE_W / 2) : 0;
    const hh = t ? Math.min(t.boxH / 2, SLIDE_H / 2) : 0;
    return {
      x: Math.min(SLIDE_W - hw, Math.max(hw, cx)) / SLIDE_W,
      y: Math.min(SLIDE_H - hh, Math.max(hh, cy)) / SLIDE_H,
    };
  };

  const onPointerDown = (e) => {
    if (!selected) return;
    const t = layoutText(stageRef.current.getContext('2d'), selected);
    if (!t) return;
    const p = toSlideCoords(e);
    const inside = Math.abs(p.x - t.cx) <= t.boxW / 2 + 20 && Math.abs(p.y - t.cy) <= t.boxH / 2 + 20;
    if (!inside) return;
    e.preventDefault();
    // Capture keeps the drag going if the pointer leaves the canvas. Some
    // browsers refuse capture for certain pointers; dragging still works.
    try { stageRef.current.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    dragRef.current = { key: selected.key, dx: p.x - t.cx, dy: p.y - t.cy };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) {
      // Show a move cursor only over the text.
      if (selected && stageRef.current) {
        const t = layoutText(stageRef.current.getContext('2d'), selected);
        const p = toSlideCoords(e);
        stageRef.current.style.cursor = t && Math.abs(p.x - t.cx) <= t.boxW / 2 && Math.abs(p.y - t.cy) <= t.boxH / 2 ? 'move' : 'default';
      }
      return;
    }
    const p = toSlideCoords(e);
    const s = slidesRef.current.find((x) => x.key === d.key);
    if (s) update(d.key, clampCentre(s, p.x - d.dx, p.y - d.dy));
  };

  const onPointerUp = (e) => {
    try {
      if (dragRef.current && stageRef.current.hasPointerCapture?.(e.pointerId)) stageRef.current.releasePointerCapture(e.pointerId);
    } catch (err) { /* ignore */ }
    dragRef.current = null;
  };

  // Arrow keys nudge the text, for precision and for keyboard users.
  const onKeyDown = (e) => {
    if (!selected || !selected.text.trim()) return;
    const step = e.shiftKey ? 40 : 8;
    const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!delta) return;
    e.preventDefault();
    update(selected.key, clampCentre(selected, selected.x * SLIDE_W + delta[0], selected.y * SLIDE_H + delta[1]));
  };

  const applyStyleToAll = () => {
    if (!selected) return;
    const { size, style, opacity, fit } = selected;
    setSlides((prev) => prev.map((s) => ({ ...s, size, style, opacity, fit })));
    toast.success('Text style applied to every slide.');
  };

  return (
    <div>
      {/* Thumbnails */}
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6, marginBottom: 12 }}>
        {slides.map((s, i) => (
          <div key={s.key} style={{ flex: '0 0 74px' }}>
            <button type="button" onClick={() => setSelectedKey(s.key)} aria-label={`Edit slide ${i + 1}`}
              style={{ position: 'relative', display: 'block', width: 74, padding: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                       border: s.key === selected?.key ? '3px solid #fe2c55' : '3px solid transparent', background: '#000' }}>
              <SlideCanvas slide={s} />
              <span style={{ position: 'absolute', top: 3, left: 3, background: 'rgba(0,0,0,.7)', color: '#fff', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700, padding: '0 5px' }}>{i + 1}</span>
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              <button type="button" aria-label={`Move slide ${i + 1} earlier`} disabled={i === 0} onClick={() => move(i, -1)} style={{ border: 'none', background: 'none', padding: 2, color: i === 0 ? '#cbd5e1' : '#475569' }}><Icon.ChevronLeft size={14} /></button>
              <button type="button" aria-label={`Remove slide ${i + 1}`} onClick={() => remove(s.key)} style={{ border: 'none', background: 'none', padding: 2, color: '#dc2626' }}><Icon.Trash2 size={13} /></button>
              <button type="button" aria-label={`Move slide ${i + 1} later`} disabled={i === slides.length - 1} onClick={() => move(i, 1)} style={{ border: 'none', background: 'none', padding: 2, color: i === slides.length - 1 ? '#cbd5e1' : '#475569' }}><Icon.ChevronRight size={14} /></button>
            </div>
          </div>
        ))}
        {slides.length > 0 && slides.length < MAX_SLIDES && (
          <div {...getRootProps()} style={{ flex: '0 0 74px', height: 131, border: `2px dashed ${isDragActive ? '#fe2c55' : '#cbd5e1'}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b', fontSize: '0.7rem', textAlign: 'center', background: '#f8fafc' }}>
            <input {...getInputProps()} />
            <Icon.Plus size={18} />Add photos
          </div>
        )}
      </div>

      {slides.length > 0 && (
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 12 }}>
          {slides.length}/{MAX_SLIDES} photos · drag the text on the preview to move it · arrow keys nudge it
        </div>
      )}

      {selected && (
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div style={{ maxWidth: 300, margin: '0 auto', borderRadius: 14, overflow: 'hidden', background: '#000', boxShadow: '0 8px 24px rgba(15,23,42,.18)', touchAction: 'none' }}>
              <SlideCanvas
                slide={selected}
                canvasRef={stageRef}
                tabIndex={0}
                aria-label={`Slide ${index + 1} preview. Drag the text to move it, or use the arrow keys.`}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
                style={{ outline: 'none' }}
              />
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>Slide {index + 1} text</div>
            <textarea className="form-control mb-3" rows={3} value={selected.text} maxLength={300}
              onChange={(e) => update(selected.key, { text: e.target.value })} placeholder="Type text to show on this slide (optional)" />

            <div style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: 4 }}>Style</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <button type="button" style={btn(selected.style === 'box')} onClick={() => update(selected.key, { style: 'box' })}>White box</button>
              <button type="button" style={btn(selected.style === 'outline')} onClick={() => update(selected.key, { style: 'outline' })}>White outline</button>
            </div>

            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem' }}>
              White {selected.style === 'box' ? 'box' : 'outline'} opacity: {Math.round(selected.opacity * 100)}%
              <input type="range" className="form-range" min={0} max={100} value={Math.round(selected.opacity * 100)}
                onChange={(e) => update(selected.key, { opacity: Number(e.target.value) / 100 })} />
            </label>

            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem' }}>
              Text size: {selected.size}px
              <input type="range" className="form-range" min={36} max={130} value={selected.size}
                onChange={(e) => update(selected.key, { size: Number(e.target.value) })} />
            </label>

            <div style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: 4 }}>Photo</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <button type="button" style={btn(selected.fit === 'fill')} onClick={() => update(selected.key, { fit: 'fill' })}>Fill screen</button>
              <button type="button" style={btn(selected.fit === 'fit')} onClick={() => update(selected.key, { fit: 'fit' })}>Show whole photo</button>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => update(selected.key, { x: 0.5, y: 0.5 })} disabled={!selected.text.trim()}>
                <Icon.Crosshair size={12} className="me-1" />Centre text
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={applyStyleToAll} disabled={slides.length < 2}>
                <Icon.Copy size={12} className="me-1" />Use this style on all slides
              </button>
            </div>
          </div>
        </div>
      )}

      {slides.length === 0 && (
        <div {...getRootProps()} style={{ border: `2px dashed ${isDragActive ? '#fe2c55' : '#cbd5e1'}`, borderRadius: 12, padding: 30, textAlign: 'center', cursor: 'pointer', background: '#f8fafc' }}>
          <input {...getInputProps()} />
          <Icon.Image size={28} color="#fe2c55" />
          <div style={{ fontWeight: 600, marginTop: 6 }}>Drag photos here, or click to choose</div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Up to {MAX_SLIDES} JPG, PNG or WebP images, in the order you want them</div>
        </div>
      )}
    </div>
  );
}

export default SlideshowEditor;
